import { ApiQueryBasePromise, ApiQueryPromise } from '@/types';
import { getResponseJwtHeader } from '@/utils/web';
import { useQuery } from '@tanstack/react-query';

import { fetchComposer } from '@nf/utils-common/compose-fetch';
import { cloudUrlParser } from '@/utils/aws-domain-util';
import queryKeys from '@/constants/query-keys';
import { useSiteStore } from '@/store/site';
import { getAuthToken } from '@/store/auth';

interface InHouseStreamingUrlResponse {
	url: string
}

export interface InHouseStreamingUrlWithSecretResponse {
	CnStreamingUrl: string;
	OtherAreaStreamingUrl: string;
}

interface GvRefreshKeyResponse {
	dispatcherkey: string
}
interface GetStreamerScheduleUrlResponse {
	data: {
		Code: number;
		Error: string[];
		ReplyTime: number;
		Result: {
			Url: string;
		}
	}
	jwtToken: string | null;
}

interface McdnDataResponse {
	StreamingUrls: string[],
	ClientId: string,
	ErrorMessage: string | null,
	TraceId: string | null,
}

export const getCloudRockStreamingUrl = async (streamingId: string, streamingSrc: string): Promise<ApiQueryBasePromise<string>> => {
	const authToken = getAuthToken();
	const apiDomain = useSiteStore.getState().apiDomain;
	const body = JSON.stringify({ streamingId: streamingId, StreamingSrc: streamingSrc });
	const response = await fetchComposer.postWithBearer(cloudUrlParser(`${apiDomain.ctcdDomain}/Streaming/GetCloudRockStreaming`), { body: body })(authToken);
	if (!response.ok) {
		throw new Error(`${response.status}: ${response.statusText}`);
	}

	return {
		data: await response.json(),
		jwtToken: getResponseJwtHeader(response)
	};
};

export const useGetCloudRockStreamingUrl = (streamingId: string, streamingSrc: string) => {
	const { data, isLoading, isError, error, refetch } = useQuery({
		queryKey: [queryKeys.STREAMING_CLOUD_ROCK],
		queryFn: () => getCloudRockStreamingUrl(streamingId, streamingSrc),
		cacheTime: 3000,
		enabled: false,
		refetchOnWindowFocus: false,
	});

	return {
		data: data?.data,
		isLoading,
		isError,
		error,
		refetch
	};
};

export const getInHouseStreamingUrl = async (sid: string): Promise<ApiQueryPromise<InHouseStreamingUrlResponse>> => {
	const authToken = getAuthToken();
	const apiDomain = useSiteStore.getState().apiDomain;
	const body = JSON.stringify({ protocol: location.protocol, sid: sid });
	const response = await fetchComposer.postWithBearer(cloudUrlParser(`${apiDomain.ctcdDomain}/Streaming/GetInHouseStreamingUrl`), { body: body })(authToken);
	if (!response.ok) {
		throw new Error(`${response.status}: ${response.statusText}`);
	}

	return {
		data: await response.json(),
		jwtToken: getResponseJwtHeader(response)
	};
};

export const useGetInHouseStreamingUrl = (sid: string) => {
	const { data, isLoading, isError, error, refetch } = useQuery({
		queryKey: [queryKeys.STREAMING_IN_HOUSE],
		queryFn: () => getInHouseStreamingUrl(sid),
		cacheTime: 3000,
		enabled: false,
		refetchOnWindowFocus: false,
	});

	return {
		data: data?.data.Data?.url,
		isLoading,
		isError,
		error,
		refetch
	};
};

export const useGetInHouseStreamingUrlWithSecret = (sid: string, apiBackendUrl: string, accessToken: string) => {
	const { data, isLoading, isError, error, refetch } = useQuery({
		queryKey: [`${queryKeys.STREAMING_IN_HOUSE_WITH_SECRET}_${sid}`],
		queryFn: () => getInHouseStreamingUrlWithSecret(sid, apiBackendUrl, accessToken),
		cacheTime: 3000,
		enabled: false,
		refetchOnWindowFocus: false,
	});

	return {
		data: data?.data.Data,
		isLoading,
		isError,
		error,
		refetch
	};
};

export const getInHouseStreamingUrlWithSecret = async (sid: string, apiBackendUrl: string, accessToken: string): Promise<ApiQueryPromise<InHouseStreamingUrlWithSecretResponse[]>> => {
	
	const body = JSON.stringify({
		'SourceId': 13,
		'StreamingId': sid
	}
	);
	const response = await fetchComposer.postWithBearer(cloudUrlParser(`${apiBackendUrl}/Streaming/GetStreamingUrl`), { body: body })(accessToken);
	if (!response.ok) {
		throw new Error(`${response.status}: ${response.statusText}`);
	}

	return {
		data: await response.json(),
		jwtToken: getResponseJwtHeader(response)
	};
};

// src 29作法同21~23 this api TODO: 也要加exclusive判斷??
export const getStreamer21Url = async (streamingId: string): Promise<ApiQueryBasePromise<string>> => {
	const authToken = getAuthToken();
	const apiDomain = useSiteStore.getState().apiDomain;
	const body = JSON.stringify({ streamingId: streamingId });
	const response = await fetchComposer.postWithBearer(cloudUrlParser(`${apiDomain.ctcdDomain}/Streaming/GetStreamer21`), { body: body })(authToken);
	if (!response.ok) {
		throw new Error(`${response.status}: ${response.statusText}`);
	}

	return {
		data: await response.json(),
		jwtToken: getResponseJwtHeader(response)
	};
};

export const useGetStreamer21Url = (streamingId: string) => {
	const { data, isLoading, isError, error, refetch } = useQuery({
		queryKey: [queryKeys.STREAMING_STREAMER21],
		queryFn: () => getStreamer21Url(streamingId),
		cacheTime: 3000,
		enabled: false,
		refetchOnWindowFocus: false,
	});

	return {
		data: data?.data,
		isLoading,
		isError,
		error,
		refetch
	};
};

export const getMCDNStreamingUrl = async (streamingId: string, isExclusive: boolean): Promise<ApiQueryBasePromise<McdnDataResponse>> => {
	const authToken = getAuthToken();
	const apiDomain = useSiteStore.getState().apiDomain;
	const body = JSON.stringify({ streamingId: streamingId, isExclusive: isExclusive });
	const response = await fetchComposer.postWithBearer(cloudUrlParser(`${apiDomain.ctcdDomain}/Streaming/GetMCDNStreamingUrl`), { body: body })(authToken);
	if (!response.ok) {
		throw new Error(`${response.status}: ${response.statusText}`);
	}

	return {
		data: await response.json(),
		jwtToken: getResponseJwtHeader(response)
	};
};

export const useGetMCDNStreamingUrl = (streamingId: string, isExclusive: boolean) => {
	const { data, isLoading, isError, error, refetch } = useQuery({
		queryKey: [queryKeys.STREAMING_MCDN],
		queryFn: () => getMCDNStreamingUrl(streamingId, isExclusive),
		cacheTime: 3000,
		enabled: false,
		refetchOnWindowFocus: false,
	});
	// console.log('useGetMCDNStreamingUrl', data?.data);
	return {
		data: data?.data,
		isLoading,
		isError,
		error,
		refetch
	};
};

export const getStreamingIndex = async (mid: number, pid: string, sid: string, hid: number, aid: number, w?: string, h?: string): Promise<ApiQueryPromise<string>> => {
	const authToken = getAuthToken();
	const apiDomain = useSiteStore.getState().apiDomain;
	const body = JSON.stringify({ protocol: location.protocol, mid: mid, pid: pid, sid: sid, hid: hid, aid: aid, width: w, height: h });
	const response = await fetchComposer.postWithBearer(cloudUrlParser(`${apiDomain.ctcdDomain}/api/Streaming/GetStreamingUrl`), { body: body })(authToken);
	if (!response.ok) {
		throw new Error(`${response.status}: ${response.statusText}`);
	}

	return {
		data: await response.json(),
		jwtToken: getResponseJwtHeader(response)
	};
};

//看起來是剩餘一些Streaming的專用api
export const useGetStreamingIndex = (mid: number, pid: string, sid: string, hid: number, aid: number, w?: string, h?: string) => {
	const { data, isLoading, isError, error, refetch } = useQuery({
		queryKey: [queryKeys.STREAMING_INDEX],
		queryFn: () => getStreamingIndex(mid, pid, sid, hid, aid, w, h),
		cacheTime: 3000,
		enabled: false,
		refetchOnWindowFocus: false,
	});

	return {
		data: data?.data.Data,
		isLoading,
		isError,
		error,
		refetch
	};
};

export const refreshGVKey = async (): Promise<ApiQueryPromise<GvRefreshKeyResponse>> => {
	const authToken = getAuthToken();
	const apiDomain = useSiteStore.getState().apiDomain;
	const response = await fetchComposer.postWithBearer(cloudUrlParser(`${apiDomain.ctcdDomain}/GameVisualization`))(authToken);
	if (!response.ok) {
		throw new Error(`${response.status}: ${response.statusText}`);
	}

	return {
		data: await response.json(),
		jwtToken: getResponseJwtHeader(response)
	};
}

export const useRefreshGVKey = () => {
	const { data, isLoading, isError, error, refetch } = useQuery({
		queryKey: [queryKeys.STREAMING_GV_KEY],
		queryFn: refreshGVKey,
		cacheTime: 3000,
		refetchOnWindowFocus: false,
	});

	return {
		data: data?.data.Data?.dispatcherkey,
		isLoading,
		isError,
		error,
		refetch
	};
};

export const getGameInfoUrl = async (matchId: number): Promise<ApiQueryPromise<string>> => {
	const authToken = getAuthToken();
	const apiDomain = useSiteStore.getState().apiDomain;
	const response = await fetchComposer.getWithBearer(cloudUrlParser(`${apiDomain.ctcdDomain}/api/ExtraInfo/GetGameInfo?matchId=${matchId}`))(authToken);

	if (!response.ok) {
		throw new Error(`${response.status}: ${response.statusText}`);
	}

	return {
		data: await response.json(),
		jwtToken: getResponseJwtHeader(response)
	};
};

export const fetchStreamerScheduleUrl = async (fetchStreamerScheduleUrlData: string, DiscoverChatroomApiUrl: string): Promise<GetStreamerScheduleUrlResponse> => {
	const authToken = getAuthToken();
	const response = await fetchComposer.postWithBearerBase(`${DiscoverChatroomApiUrl}/Streamer/V3/GetStreamerScheduleUrl`, {
		body: fetchStreamerScheduleUrlData,
		headers: {
			'Content-Type': 'application/json',
			'Accept': 'application/json'
		}
	})(authToken);
	if (!response.ok) {
		throw new Error(`${response.status}: ${response.statusText}`);
	}
	return {
		data: await response.json(),
		jwtToken: getResponseJwtHeader(response)
	};
}
export const useFetchStreamerScheduleUrl = (fetchStreamerScheduleUrlData: string, DiscoverChatroomApiUrl: string) => {
	const { data, isLoading, isError, error, refetch } = useQuery({
		queryKey: [queryKeys.GET_STREAMING_SCHEDULE],
		queryFn: () => fetchStreamerScheduleUrl(fetchStreamerScheduleUrlData, DiscoverChatroomApiUrl),
		cacheTime: 3000,
		enabled: false,
		refetchOnWindowFocus: false,
	});

	return {
		data: data?.data,
		isLoading,
		isError,
		error,
		refetch
	};
};
